/**
 * 저장 상태 문구 판정 (REV-006, R2-REV-005/006).
 *
 * 화면 컴포넌트에서 떼어 순수 함수로 둔다. 무엇을 `저장 완료`라고 부를지가 참가자에게
 * 가장 무거운 약속이라, 조건을 검사로 고정한다.
 */

import type { SenderStatus } from '../online/sender';
import { collectKo as t } from '../strings/collect';

export interface SavedGate {
  /** 서버가 최종 응답까지 받아 완료로 확정했는가. */
  readonly submitted: boolean;
  /** 확정된 단일 결과 envelope을 이 화면이 실제로 검증했는가 (REV-007). */
  readonly resultVerified: boolean;
}

/**
 * 저장 상태 문구 (REV-006).
 *
 * `저장 완료`는 **세 가지가 모두 참일 때만** 나온다: 서버가 완료를 확정했고, 이
 * 화면이 확정 결과 문서를 검증했고, 보내지 못한 이벤트·경로 조각·저장 확인 기록과
 * 보내던 batch가 하나도 없다. 예전에는 보낼 것이 없다는 사실만으로 완료를 표시해서,
 * 제출도 하기 전에 참가자가 끝난 것으로 오해할 수 있었다.
 *
 * 그리고 **이번 세션의 저장 확인 기록을 버리지 않았어야** 한다 (R2-REV-005/006). 버린
 * 기록은 대기열에 없어 `synced`를 막지 않고, 확정 결과 문서에도 적히지 않는다. 그 사실을
 * 완료 문구로 덮지 않고 따로 밝힌다. 버퍼는 세션 로그마다 따로라서 연습·앞선 세션의
 * 손실은 이 수에 들어오지 않는다.
 */
export function statusLabel(status: SenderStatus | undefined, gate: SavedGate): string {
  const settled = status !== undefined && status.synced;
  if (gate.submitted && gate.resultVerified && settled) {
    // 확정된 뒤의 마지막 오류를 실패로 보이지 않게 한다. 남은 자료가 없을 때만이다.
    // 확인 불가가 손실보다 나쁘다. 손실 수가 함께 있어도 확인 불가로 말한다(CR-01 A+ §2.5).
    if (status.confirmationsState === 'unverified') {
      return t.status.savedUnverified;
    }
    return status.droppedConfirmations > 0 ? t.status.savedWithLoss(status.droppedConfirmations) : t.status.saved;
  }
  if (status === undefined) {
    return t.status.sending;
  }
  switch (status.state) {
    case 'sending':
      return t.status.sending;
    case 'offline':
      return t.status.offline;
    case 'readonly':
      return t.status.readonly;
    case 'blocked':
      return `${t.status.blocked} (${status.lastError ?? ''})`;
    case 'pending':
      return t.status.pending;
    case 'idle':
      if (!status.synced) {
        return t.status.pending;
      }
      return gate.submitted ? t.status.confirming : t.status.beforeSubmit;
    default:
      return t.status.pending;
  }
}

export interface DoneView {
  readonly heading: string;
  readonly note: string;
}

/** 완료 화면의 제목과 안내. 확정 결과 문서를 검증하기 전에는 `저장 완료`라고 쓰지 않는다. */
export function doneView(status: SenderStatus | undefined, resultVerified: boolean): DoneView {
  if (!resultVerified) {
    return { heading: t.status.confirming, note: t.result.unverified };
  }
  if (status === undefined || !status.synced) {
    return { heading: t.status.confirming, note: t.status.savedNote };
  }
  if (status.confirmationsState === 'unverified') {
    return { heading: t.status.savedUnverified, note: t.status.unverifiedNote };
  }
  if (status.droppedConfirmations > 0) {
    return { heading: t.status.savedWithLoss(status.droppedConfirmations), note: t.status.lossNote };
  }
  return { heading: t.status.saved, note: t.status.savedNote };
}
